/** Map object sprites: placed from the ground line, sorted by it, culled by it.
 *
 * Everything that stands on the map goes through this layer - resources, mines,
 * dwellings, banks, towns, boats and heroes - because they all have to sort
 * against each other. A hero walking south past a castle must pass in front of
 * it on the near side and behind it on the far side, and that only works if
 * both are in the same sorted list.
 *
 * Placement uses the frame's `anchorX`/`anchorY`, which the packer set to the
 * bottom-centre of the opaque box. It is emphatically not the bounding-box
 * corner: a 6-tile castle and a coin heap have wildly different transparent
 * margins, and anchoring by the corner puts one of them in the air.
 */

import { Container, Rectangle, Sprite, Texture } from "pixi.js";
import type { Atlas } from "../atlas.js";
import type { Drawable } from "./depth.js";
import { cullToRect, sortByGroundLine } from "./depth.js";
import type { FrameMeta, PixelRect } from "./frames.js";
import { frameBounds } from "./frames.js";
import { ownerVariantKey, playerTint } from "./keys.js";
import type { ShadowLayer } from "./shadows.js";
import { shadowStrength } from "./shadows.js";
import { TILE_SIZE } from "../coords.js";

/**
 * How far a mountain-range piece is drawn BELOW its ground line, in world px.
 *
 * `range-kit.py` dissolves the foot of every range piece into scattered stones
 * along a noise contour instead of cutting it flat, and the ground line is the
 * cell's southern edge - so drawn on the line, the ragged foot sits wholly on
 * the rock cell's own gravel and the straight terrain edge under it still
 * shows. Dropped a few pixels, the scree spills onto the neighbouring terrain
 * and hides that edge, which is how a classic range meets the ground. The sort
 * key is untouched: the piece is still ordered by its cell, so a hero on the
 * row south still passes in front of it and one on the row north behind.
 *
 * 12 until 2026-09-08, when `range-kit.py` began baking a cast shadow into
 * every range piece and grew its frame by SHADOW_PAD (26) rows at the bottom
 * to hold it. The manifest anchors a piece by its frame's BOTTOM
 * (`drawOffsetPx` y = (rows-1)*64 - spritePx.h), so a 26px taller frame with
 * the same art in it would ride 26px UP the screen; the spill absorbs exactly
 * that and the rock lands where it did. The two numbers move together - the
 * pad in range-kit.py and this - and nothing else changes.
 */
export const RIDGE_SPILL = 38;
const RIDGE_KEY = "obj.obs_mtn_";

/** How tall the owner pennant is, as a fraction of the sprite it flies over. */
const FLAG_HEIGHT_RATIO = 0.16;
/** Where the pennant's pole foot lands, as a fraction of the sprite's box. */
const FLAG_AT_X = 0.20;
const FLAG_AT_Y = 0.17;

/**
 * Tiles a frame covers across, from its UNTRIMMED source width.
 *
 * The art is authored one tile to sixty-four source pixels, so a 384px castle
 * is a six-tile building and a 128px mine is a two-tile one. Trimming crops the
 * transparent margin off the packed frame, so `srcW` - not `w` - is the width
 * the artist drew to, and rounding it is exact for every frame in the set.
 */
export function spriteTilesWide(f: FrameMeta): number {
  return Math.max(1, Math.round((f.srcW > 0 ? f.srcW : f.w) / TILE_SIZE));
}

/**
 * The ground-line X of a multi-cell object standing with its footprint's
 * left column on cell `cellX`.
 *
 * Every `MapObjectDef` in the content set anchors its footprint at the
 * bottom-LEFT cell and extends right and up (`mapgen/place.ts`
 * `footprintCells`), so a six-tile castle placed at x=13 covers x=13..18 and
 * must be drawn centred on x=16, not on x=13. Using the anchor cell's own
 * centre - which is what `cellGround` gives - slides every object of width
 * `n` half of `n-1` tiles to the west: 32px for a mine, 160px for a town, and
 * that is why a road laid to a town's entrance missed its gate.
 *
 * For a one-tile object this is exactly `cellGround`, so nothing else moves.
 */
export function footprintGroundX(cellX: number, tilesWide: number): number {
  return (cellX + tilesWide / 2) * TILE_SIZE;
}

/** Draw layers inside one cell; higher draws later. */
/** The frame a cropped flag actually occupies: the same box, shorter, and with
 *  its ground anchor moved up with the cut edge. */
function croppedFrame(f: FrameMeta | null, crop: number): FrameMeta | null {
  if (f === null) return null;
  if (crop >= 1) return f;
  const h = Math.max(1, Math.round(f.h * crop));
  return { ...f, h, anchorY: h };
}

export const LAYER_FLAT = 0;
export const LAYER_OBJECT = 10;
export const LAYER_TOWN = 12;
export const LAYER_BOAT = 14;
export const LAYER_HERO = 20;

export interface WorldSprite extends Drawable {
  readonly key: string;
  readonly frame: FrameMeta;
  readonly tint: number;
  readonly alpha: number;
  /** An owner banner drawn on top, when the art has no baked owner variant. */
  readonly flagKey: string | null;
  readonly flagTint: number;
  /** Pennant height in world pixels. */
  readonly flagHeight: number;
  /** Where the pennant's ground point sits, as a fraction of the sprite box. */
  readonly flagAtX: number;
  readonly flagAtY: number;
  readonly flagBehind: boolean;
  /** Fraction of the flag frame kept, from its top. 1 draws the whole frame. */
  readonly flagCrop: number;
  /** 0..1; 0 suppresses the contact shadow entirely. */
  readonly shadow: number;
}

export interface SpriteRequest {
  readonly id: string;
  readonly key: string;
  readonly groundX: number;
  readonly groundY: number;
  readonly layer: number;
  readonly tint?: number;
  readonly alpha?: number;
  readonly flagKey?: string | null;
  readonly flagTint?: number;
  /** Pennant height in world pixels; defaults to a fraction of the sprite. */
  readonly flagHeight?: number;
  /** Where the pennant's own ground point sits, as a fraction of the sprite box. */
  readonly flagAtX?: number;
  readonly flagAtY?: number;
  /** Draw the pennant under the sprite, so a pole reads as planted behind it. */
  readonly flagBehind?: boolean;
  /** Keep only this fraction of the flag frame, measured from its top. */
  readonly flagCrop?: number;
  readonly shadow?: number;
}

/** `null` when the atlas has no such key, which is a content gap, not a crash. */
export function makeWorldSprite(atlas: Atlas, req: SpriteRequest): WorldSprite | null {
  const packed = atlas.frame(req.key);
  if (packed === null) return null;
  // Moving the anchor up the frame draws the art lower for the same ground
  // line, so the spill costs nothing in sorting or culling.
  const ridge = req.key.startsWith(RIDGE_KEY);
  const frame = ridge
    ? { ...packed, anchorY: packed.anchorY - RIDGE_SPILL }
    : packed;
  // A range piece carries its own baked cast shadow (`range-kit.py`
  // `_cast_shadow`) and takes none from the computed layer. The computed
  // shadow shears a sprite's silhouette up-screen and to the left by four
  // fifths of its height, which for a 150px massif standing in a wall lands
  // entirely on the massif's own art or on the piece north of it - the last
  // verdict looked for a shadow under every one of ours and found the ground
  // "fully lit right up to the rock on all four sides". Baked, it lies where
  // a wall actually leaves ground visible. Casting both would darken the
  // baked lobe a second time and give the frame two suns.
  const shadow = ridge ? 0 : req.shadow;
  // A range piece stands on ONE cell whatever its width, centred on it.
  // The presenter centres a sprite over `spriteTilesWide` cells from its
  // anchor eastward, which is right for a castle and wrong for a solo cut
  // wide at the foot (`range-kit.py` SOLO_OVER: 120px, so two steps of a
  // diagonal staircase overlap at the corner) - read as a two-tile sprite
  // it was drawn half a tile east of its rock. `tools/ridge-shot.py`
  // centres range pieces the same way.
  const groundX = ridge
    ? req.groundX - ((spriteTilesWide(frame) - 1) * TILE_SIZE) / 2
    : req.groundX;
  return {
    id: req.id,
    key: req.key,
    frame,
    groundX,
    groundY: req.groundY,
    layer: req.layer,
    bounds: frameBounds(groundX, req.groundY, frame),
    tint: req.tint ?? 0xffffff,
    alpha: req.alpha ?? 1,
    flagKey: req.flagKey ?? null,
    flagTint: req.flagTint ?? 0xffffff,
    flagHeight: req.flagHeight
      ?? Math.max(16, Math.min(40, frame.h * FLAG_HEIGHT_RATIO)),
    flagAtX: req.flagAtX ?? FLAG_AT_X,
    flagAtY: req.flagAtY ?? FLAG_AT_Y,
    flagBehind: req.flagBehind ?? false,
    flagCrop: Math.max(0.05, Math.min(1, req.flagCrop ?? 1)),
    shadow: shadow ?? shadowStrength(frame, TILE_SIZE),
  };
}

/**
 * Prefer the baked owner variant; fall back to the neutral sprite plus a tinted
 * banner. The baked variants exist for towns, garrisons, monoliths, border
 * gates, guardhouses, rally flags and banners; everything else needs the flag.
 */
export function resolveOwned(
  atlas: Atlas, baseKey: string, colour: string | null,
): { readonly key: string; readonly flagKey: string | null; readonly flagTint: number } {
  const variant = ownerVariantKey(baseKey, colour);
  if (variant !== null && atlas.has(variant)) {
    return { key: variant, flagKey: null, flagTint: 0xffffff };
  }
  if (colour === null) return { key: baseKey, flagKey: null, flagTint: 0xffffff };
  const bakedFlag = ownerVariantKey("obj.pu_banner", colour);
  if (bakedFlag !== null && atlas.has(bakedFlag)) {
    return { key: baseKey, flagKey: bakedFlag, flagTint: 0xffffff };
  }
  return { key: baseKey, flagKey: "obj.pu_banner", flagTint: playerTint(colour) };
}

interface Entry {
  readonly sprite: Sprite;
  readonly flag: Sprite | null;
}

export class ObjectLayer {
  readonly container = new Container({ label: "objects" });
  private readonly pool = new Map<string, Entry>();

  constructor(private readonly atlas: Atlas) {}

  /**
   * Reconcile the layer against this frame's sprite list.
   *
   * Culling is against each sprite's own drawn bounds rather than its cell,
   * because a tall object anchored below the viewport still reaches into it;
   * `pad` covers sub-pixel and walk-animation offsets.
   */
  sync(
    sprites: readonly WorldSprite[], view: PixelRect, shadows: ShadowLayer | null,
    pad = TILE_SIZE,
  ): number {
    const visible = sortByGroundLine(cullToRect(sprites, view, pad));

    const live = new Set<string>();
    this.container.removeChildren();
    if (shadows !== null) shadows.begin();

    for (const s of visible) {
      live.add(s.id);
      let entry = this.pool.get(s.id);
      if (entry === undefined || entry.sprite.texture.label !== s.key) {
        entry?.sprite.destroy();
        entry?.flag?.destroy();
        entry = {
          sprite: new Sprite(this.atlas.texture(s.key)),
          flag: this.flagSprite(s),
        };
        this.pool.set(s.id, entry);
      }

      const b = frameBounds(s.groundX, s.groundY, s.frame);
      // Round in world space so the sprite lands on a whole world pixel, which
      // the snapped camera then maps to a whole device pixel.
      entry.sprite.position.set(Math.round(b.x), Math.round(b.y));
      entry.sprite.tint = s.tint;
      entry.sprite.alpha = s.alpha;
      if (!s.flagBehind) this.container.addChild(entry.sprite);

      if (entry.flag !== null) {
        const ff = croppedFrame(this.atlas.frame(s.flagKey ?? ""), s.flagCrop);
        if (ff !== null) {
          // `obj.pu_banner` is a full-tile map object in its own right; used as
          // an owner marker it has to be scaled to a pennant, sized against the
          // thing it flies over rather than to a constant, so a castle gets a
          // standard and a hero gets a lance pennon.
          const scale = s.flagHeight / ff.h;
          entry.flag.scale.set(scale);
          // This is where the pennant's POLE MEETS THE GROUND, not where its
          // cloth starts: `obj.pu_banner` is ground-anchored art like anything
          // else. Pinned inside the opaque box rather than to the sprite's
          // centre, because a pennant flying off the middle of a mine reads as
          // litter rather than as a claim.
          const fx = b.x + s.frame.w * s.flagAtX;
          const fy = b.y + s.frame.h * s.flagAtY;
          entry.flag.position.set(
            Math.round(fx - ff.anchorX * scale), Math.round(fy - ff.anchorY * scale),
          );
          entry.flag.tint = s.flagTint;
          entry.flag.alpha = s.alpha;
          this.container.addChild(entry.flag);
        }
      }
      if (s.flagBehind) this.container.addChild(entry.sprite);

      if (shadows !== null && s.shadow > 0) {
        // The shadow is the caster's own silhouette, so it needs the caster's
        // texture - the one already resolved for `entry.sprite`.
        shadows.add({
          groundX: s.groundX, groundY: s.groundY, frame: s.frame,
          texture: entry.sprite.texture, strength: s.shadow,
        });
      }
    }

    if (shadows !== null) shadows.end();

    for (const [id, entry] of this.pool) {
      if (live.has(id)) continue;
      entry.sprite.destroy();
      entry.flag?.destroy();
      this.pool.delete(id);
    }
    return visible.length;
  }

  /**
   * The owner flag as a sprite, cropped if the caller asked for part of it.
   *
   * Cropping exists because the only upright standard in the set is
   * `obj.bld_rally_flag`, which is a pole planted in a cairn of stones: the
   * pole and the cloth are exactly the owner marker a hero wants and the cairn
   * is not. A `Texture` over a sub-rectangle of the same `TextureSource` costs
   * nothing - no upload, no copy - so the top of the frame is simply a
   * different window onto the sheet the atlas already holds.
   */
  private flagSprite(s: WorldSprite): Sprite | null {
    if (s.flagKey === null || !this.atlas.has(s.flagKey)) return null;
    const base = this.atlas.texture(s.flagKey);
    if (s.flagCrop >= 1) return new Sprite(base);
    const f = this.atlas.frame(s.flagKey);
    if (f === null) return new Sprite(base);
    return new Sprite(new Texture({
      source: base.source,
      frame: new Rectangle(f.x, f.y, f.w, Math.max(1, Math.round(f.h * s.flagCrop))),
      label: `${s.flagKey}#crop`,
    }));
  }

  destroy(): void {
    for (const entry of this.pool.values()) {
      entry.sprite.destroy();
      entry.flag?.destroy();
    }
    this.pool.clear();
    this.container.destroy({ children: true, context: true });
  }
}
